/**
 * Workbook adapter.
 *
 * Loads the normalized dataset generated by
 * `scripts/import_workbook.mjs` and exposes it through a stable
 * interface. This adapter is the ONLY module that knows the JSON file
 * exists — everything else in the application talks to the repository.
 *
 * Swapping to a live backend later is a matter of implementing the
 * same interface (loadCollections + getCollection) in an alternative
 * adapter and re-exporting it from `data/adapters/index.js`.
 */
import workbook from "@/data/workbook.json";

let CACHE = null;

function build() {
  if (CACHE) return CACHE;
  // Everything is already normalized at import-time. We simply index
  // each collection by its persistent ID for O(1) lookups; foreign-key
  // resolution stays lazy so provenance is preserved as IDs, not
  // embedded objects.
  const idx = (rows, key) => {
    const m = new Map();
    for (const r of rows) if (r[key]) m.set(r[key], r);
    return m;
  };

  CACHE = {
    meta: workbook.meta,
    collections: {
      departments:       workbook.departments        || [],
      people:            workbook.people             || [],
      surgeonProfiles:   workbook.surgeonProfiles    || [],
      cases:             workbook.cases              || [],
      caseParticipants:  workbook.caseParticipants   || [],
      surgicalDetails:   workbook.surgicalDetails    || [],
      encounters:        workbook.encounters         || [],
      timelineEvents:    workbook.timelineEvents     || [],
      reflections:       workbook.reflections        || [],
      operativePearls:   workbook.operativePearls    || [],
      imaging:           workbook.imaging            || [],
      mmRecords:         workbook.mmRecords          || [],
      collaboration:     workbook.collaboration      || [],
      literature:        workbook.literature         || [],
      derivedInsights:   workbook.derivedInsights    || [],
      suggestedQuestions:workbook.suggestedQuestions || [],
    },
    indices: {
      personById:            idx(workbook.people,             "personId"),
      surgeonProfileById:    idx(workbook.surgeonProfiles,    "personId"),
      caseById:              idx(workbook.cases,              "caseId"),
      surgicalDetailById:    idx(workbook.surgicalDetails,    "surgicalDetailId"),
      encounterById:         idx(workbook.encounters,         "encounterId"),
      timelineEventById:     idx(workbook.timelineEvents,     "timelineEventId"),
      reflectionById:        idx(workbook.reflections,        "reflectionId"),
      operativePearlById:    idx(workbook.operativePearls,    "operativePearlId"),
      imagingById:           idx(workbook.imaging,            "imagingId"),
      mmRecordById:          idx(workbook.mmRecords,          "mAndMId"),
      collaborationById:     idx(workbook.collaboration,      "collaborationId"),
      literatureById:        idx(workbook.literature,         "literatureId"),
      derivedInsightById:    idx(workbook.derivedInsights,    "insightId"),
      suggestedQuestionById: idx(workbook.suggestedQuestions, "questionId"),
    },
  };
  return CACHE;
}

/* Public adapter interface — any future adapter must match this. */
export const workbookAdapter = {
  name: "workbook",
  meta:            () => build().meta,
  collection:      (name) => build().collections[name] || [],
  index:           (name) => build().indices[name] || new Map(),
  byId:            (name, id) => build().indices[name]?.get(id) || null,
};
